import { useState } from "react";
import {
  updateProfile,
  submitPlacement,
  type CefrLevel,
  type UserRow,
} from "../lib/api";

type Step =
  | "welcome"
  | "track"
  | "examDetail"
  | "personal"
  | "placement"
  | "result";

interface Props {
  teacherName: string;
  onDone: (user: UserRow) => void;
}

interface PlacementQ {
  level: CefrLevel;
  question: string;
  hint: string;
}

const PLACEMENT: PlacementQ[] = [
  { level: "A1", question: "What is your name and where are you from?",         hint: "A short sentence is fine — just say it naturally." },
  { level: "A2", question: "Tell me about your last weekend. What did you do?", hint: "Try to use past-tense verbs." },
  { level: "B1", question: "What do you do for work or study, and what do you like about it?", hint: "A few sentences — try to explain why." },
  { level: "B2", question: "Describe a difficult decision you had to make recently. How did you decide?", hint: "Use any complex language you're comfortable with." },
];

interface TrackOption {
  value: string;
  emoji: string;
  label: string;
  blurb: string;
  isExam?: boolean;
}

const TRACKS: TrackOption[] = [
  { value: "conversation", emoji: "💬", label: "Conversational fluency", blurb: "Daily chat, small talk, sounding natural." },
  { value: "work",         emoji: "💼", label: "Business / work",         blurb: "Meetings, emails, presentations, negotiation." },
  { value: "travel",       emoji: "✈️",  label: "Travel",                  blurb: "Airport, hotel, restaurant, asking around." },
  { value: "exam_ielts",   emoji: "🎓",  label: "IELTS prep",              blurb: "Speaking + Writing bands, academic register.", isExam: true },
  { value: "exam_toefl",   emoji: "🎓",  label: "TOEFL prep",              blurb: "Integrated tasks, time-pressured speaking.", isExam: true },
  { value: "exam_cambridge", emoji: "🎓", label: "Cambridge (B2/C1/C2)",   blurb: "FCE / CAE / CPE Speaking + Writing format.", isExam: true },
  { value: "academic",     emoji: "📖", label: "Academic",                blurb: "University lectures, papers, formal register." },
  { value: "kids",         emoji: "🧸", label: "Kids (under 12)",         blurb: "Playful, repetitive, simple words." },
];

const INTEREST_CHIPS = [
  "tech", "sports", "music", "films", "books", "gaming", "food", "travel",
  "business", "science", "history", "art", "fashion", "fitness", "family",
  "politics", "cars", "nature",
];

const LEVELS: CefrLevel[] = ["A1", "A2", "B1", "B2", "C1", "C2"];

const LEVEL_BLURB: Record<CefrLevel, string> = {
  A1: "Beginner — simple phrases for basic needs.",
  A2: "Elementary — everyday situations.",
  B1: "Intermediate — familiar topics, plans.",
  B2: "Upper-intermediate — most conversations easily.",
  C1: "Advanced — fluent, flexible.",
  C2: "Mastery — natural on any topic.",
};

export function Onboarding({ teacherName, onDone }: Props): JSX.Element {
  const [step, setStep] = useState<Step>("welcome");
  const [name, setName] = useState("");
  const [track, setTrack] = useState<string>("conversation");
  const [examName, setExamName] = useState("");
  const [examDate, setExamDate] = useState<string>(""); // YYYY-MM-DD
  const [profession, setProfession] = useState("");
  const [interests, setInterests] = useState<string[]>([]);
  const [targetLevel, setTargetLevel] = useState<CefrLevel>("B2");
  const [answers, setAnswers] = useState<string[]>(PLACEMENT.map(() => ""));
  const [qIdx, setQIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ level: CefrLevel; user: UserRow } | null>(null);

  const totalSteps = 5; // welcome, track, personal, placement, result (examDetail is conditional and counts as part of track)

  function toggleInterest(tag: string): void {
    setInterests((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : prev.length >= 8 ? prev : [...prev, tag],
    );
  }

  async function commitWelcome(): Promise<void> {
    if (!name.trim()) {
      setError("Please tell me your name.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await updateProfile({ name: name.trim() });
      setStep("track");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function commitTrack(): Promise<void> {
    setSubmitting(true);
    try {
      await updateProfile({ goal_track: track, goal: TRACKS.find((t) => t.value === track)?.label ?? track });
      const opt = TRACKS.find((t) => t.value === track);
      setStep(opt?.isExam ? "examDetail" : "personal");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function commitExamDetail(): Promise<void> {
    setSubmitting(true);
    try {
      const ts = examDate ? new Date(examDate).getTime() : undefined;
      await updateProfile({
        exam_name: examName || undefined,
        exam_date: ts,
      });
      setStep("personal");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function commitPersonal(): Promise<void> {
    setSubmitting(true);
    try {
      await updateProfile({
        profession: profession.trim() || undefined,
        interests,
        target_level: targetLevel,
      });
      setStep("placement");
      setQIdx(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitAnswer(): Promise<void> {
    if (!answers[qIdx].trim()) {
      setError("Type at least a short answer to continue.");
      return;
    }
    setError(null);
    if (qIdx < PLACEMENT.length - 1) {
      setQIdx(qIdx + 1);
      return;
    }
    setSubmitting(true);
    try {
      const r = await submitPlacement(PLACEMENT.map((q, i) => ({ question: q.question, answer: answers[i] })));
      setResult({ level: r.level, user: r.user });
      setStep("result");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  function skipPlacement(): void {
    setSubmitting(true);
    submitPlacement([{ question: "Skipped", answer: "Hello, my name is " + name + "." }])
      .then((r) => {
        setResult({ level: r.level, user: r.user });
        setStep("result");
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setSubmitting(false));
  }

  // Step indicator
  const stepNum =
    step === "welcome" ? 1
    : step === "track" ? 2
    : step === "examDetail" ? 2
    : step === "personal" ? 3
    : step === "placement" ? 4
    : 5;

  const stepLabel =
    step === "welcome" ? "Your name"
    : step === "track" ? "Choose a track"
    : step === "examDetail" ? "Exam details"
    : step === "personal" ? "About you"
    : step === "placement" ? `Placement · ${qIdx + 1}/${PLACEMENT.length}`
    : "All set";

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 p-4 overflow-y-auto">
      <div className="w-full max-w-2xl rounded-2xl border border-ink-700 bg-ink-900/95 shadow-2xl">
        <div className="px-6 pt-6 pb-2 border-b border-ink-700">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-ink-500">
              Step {Math.min(stepNum, totalSteps)} of {totalSteps} · {stepLabel}
            </div>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div
                  key={i}
                  className={`h-1 w-6 rounded-full transition ${
                    i + 1 <= stepNum ? "bg-lime-400" : "bg-ink-700"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-6 space-y-5">
          {/* WELCOME */}
          {step === "welcome" && (
            <>
              <h1 className="text-xl font-semibold text-white">
                Hi, I'm {teacherName}. What should I call you?
              </h1>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void commitWelcome(); }}
                placeholder="Your first name"
                className="w-full rounded-xl bg-ink-800 border border-ink-700 px-4 py-3 text-white placeholder-ink-500 focus:outline-none focus:border-lime-500"
                autoFocus
              />
              <div className="flex justify-end">
                <button
                  onClick={() => void commitWelcome()}
                  disabled={submitting}
                  className="rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold px-5 py-2.5 disabled:opacity-50"
                >
                  Continue →
                </button>
              </div>
            </>
          )}

          {/* TRACK */}
          {step === "track" && (
            <>
              <h1 className="text-xl font-semibold text-white">
                What are you learning English for?
              </h1>
              <p className="text-sm text-ink-400">
                I'll tune lessons to this. You can change it later in settings.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {TRACKS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTrack(opt.value)}
                    className={`text-left rounded-xl border px-4 py-3 transition ${
                      track === opt.value
                        ? "border-lime-500 bg-lime-500/10"
                        : "border-ink-700 bg-ink-800/50 hover:border-ink-600"
                    }`}
                  >
                    <div className="text-base font-medium text-white">
                      <span className="mr-2">{opt.emoji}</span>{opt.label}
                    </div>
                    <div className="text-xs text-ink-400 mt-0.5">{opt.blurb}</div>
                  </button>
                ))}
              </div>
              <div className="flex justify-between">
                <button
                  onClick={() => setStep("welcome")}
                  className="text-xs text-ink-400 hover:text-white"
                >
                  ← Back
                </button>
                <button
                  onClick={() => void commitTrack()}
                  disabled={submitting}
                  className="rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold px-5 py-2.5 disabled:opacity-50"
                >
                  Continue →
                </button>
              </div>
            </>
          )}

          {/* EXAM DETAIL */}
          {step === "examDetail" && (
            <>
              <h1 className="text-xl font-semibold text-white">Exam details</h1>
              <p className="text-sm text-ink-400">
                Helps me prioritize. You can leave the date empty.
              </p>
              <label className="block">
                <div className="text-xs uppercase tracking-widest text-ink-400 mb-1">Exam name</div>
                <input
                  value={examName}
                  onChange={(e) => setExamName(e.target.value)}
                  placeholder={track === "exam_ielts" ? "IELTS Academic" : track === "exam_toefl" ? "TOEFL iBT" : "FCE / CAE / CPE"}
                  className="w-full rounded-xl bg-ink-800 border border-ink-700 px-4 py-3 text-white placeholder-ink-500 focus:outline-none focus:border-lime-500"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase tracking-widest text-ink-400 mb-1">Exam date (optional)</div>
                <input
                  type="date"
                  value={examDate}
                  onChange={(e) => setExamDate(e.target.value)}
                  className="w-full rounded-xl bg-ink-800 border border-ink-700 px-4 py-3 text-white focus:outline-none focus:border-lime-500"
                />
              </label>
              <div className="flex justify-between">
                <button
                  onClick={() => setStep("track")}
                  className="text-xs text-ink-400 hover:text-white"
                >
                  ← Back
                </button>
                <button
                  onClick={() => void commitExamDetail()}
                  disabled={submitting}
                  className="rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold px-5 py-2.5 disabled:opacity-50"
                >
                  Continue →
                </button>
              </div>
            </>
          )}

          {/* PERSONAL */}
          {step === "personal" && (
            <>
              <h1 className="text-xl font-semibold text-white">A bit about you</h1>
              <p className="text-sm text-ink-400">
                Optional, but I'll use it to keep conversations relevant.
              </p>

              <label className="block">
                <div className="text-xs uppercase tracking-widest text-ink-400 mb-1">
                  Profession or main activity
                </div>
                <input
                  value={profession}
                  onChange={(e) => setProfession(e.target.value)}
                  placeholder="e.g. backend engineer, doctor, student of law"
                  className="w-full rounded-xl bg-ink-800 border border-ink-700 px-4 py-3 text-white placeholder-ink-500 focus:outline-none focus:border-lime-500"
                />
              </label>

              <div>
                <div className="text-xs uppercase tracking-widest text-ink-400 mb-2">
                  Pick a few interests (up to 8)
                </div>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_CHIPS.map((tag) => {
                    const on = interests.includes(tag);
                    return (
                      <button
                        key={tag}
                        onClick={() => toggleInterest(tag)}
                        className={`rounded-full border px-3 py-1.5 text-sm transition ${
                          on
                            ? "border-lime-500 bg-lime-500/15 text-lime-200"
                            : "border-ink-700 bg-ink-800/50 text-ink-300 hover:border-ink-600"
                        }`}
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-ink-400 mb-2">
                  Where do you want to get? (target level)
                </div>
                <div className="flex flex-wrap gap-2">
                  {LEVELS.map((lvl) => (
                    <button
                      key={lvl}
                      onClick={() => setTargetLevel(lvl)}
                      className={`rounded-xl border px-4 py-2 transition ${
                        targetLevel === lvl
                          ? "border-lime-500 bg-lime-500/15 text-white"
                          : "border-ink-700 bg-ink-800/50 text-ink-300 hover:border-ink-600"
                      }`}
                    >
                      <div className="font-medium">{lvl}</div>
                      <div className="text-[10px] opacity-75">{LEVEL_BLURB[lvl].split(" — ")[1]}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(TRACKS.find((t) => t.value === track)?.isExam ? "examDetail" : "track")}
                  className="text-xs text-ink-400 hover:text-white"
                >
                  ← Back
                </button>
                <button
                  onClick={() => void commitPersonal()}
                  disabled={submitting}
                  className="rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold px-5 py-2.5 disabled:opacity-50"
                >
                  Continue →
                </button>
              </div>
            </>
          )}

          {/* PLACEMENT */}
          {step === "placement" && (
            <>
              <h1 className="text-xl font-semibold text-white">{PLACEMENT[qIdx].question}</h1>
              <p className="text-sm text-ink-400">{PLACEMENT[qIdx].hint}</p>
              <textarea
                value={answers[qIdx]}
                onChange={(e) => {
                  const next = [...answers];
                  next[qIdx] = e.target.value;
                  setAnswers(next);
                }}
                rows={4}
                className="w-full rounded-xl bg-ink-800 border border-ink-700 px-4 py-3 text-white placeholder-ink-500 focus:outline-none focus:border-lime-500"
                placeholder="Type in English…"
                autoFocus
              />
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <button
                  onClick={skipPlacement}
                  className="text-xs text-ink-400 hover:text-white"
                >
                  Skip placement, I'll figure it out later
                </button>
                <button
                  onClick={() => void submitAnswer()}
                  disabled={submitting}
                  className="rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold px-5 py-2.5 disabled:opacity-50"
                >
                  {qIdx < PLACEMENT.length - 1 ? "Next question →" : submitting ? "Analyzing…" : "Finish placement"}
                </button>
              </div>
            </>
          )}

          {/* RESULT */}
          {step === "result" && result && (
            <>
              <h1 className="text-xl font-semibold text-white">You're {result.level}</h1>
              <p className="text-sm text-ink-300">{LEVEL_BLURB[result.level]}</p>
              <div className="rounded-xl border border-ink-700 bg-ink-800/60 p-4 text-sm">
                <div className="text-ink-400">Your track</div>
                <div className="text-white">{TRACKS.find((t) => t.value === track)?.label}</div>
                {targetLevel && (
                  <>
                    <div className="text-ink-400 mt-2">Target level</div>
                    <div className="text-white">{targetLevel}</div>
                  </>
                )}
              </div>
              <button
                onClick={() => onDone(result.user)}
                className="w-full rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold py-3"
              >
                Start learning with {teacherName}
              </button>
            </>
          )}

          {error && (
            <div className="bg-rose-900/40 border border-rose-700 text-rose-200 rounded-xl px-4 py-2 text-xs">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
